import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'

export interface ProfileMember {
  id: string
  email: string | null
  full_name: string | null
  company_name: string | null
  role: 'owner' | 'admin' | 'developer' | 'manager' | 'member' | 'viewer' | 'client'
  client_id: string | null
  client_ids: string[]
  permissions: Record<string, any>
  created_at: string
  updated_at: string
  invitation_accepted_at: string | null
}

export interface ClientRecord {
  id: string
  name: string
  client_id: number
  status: string | null
}

export function useProfiles() {
  return useQuery({
    queryKey: ['profiles'],
    queryFn: async (): Promise<ProfileMember[]> => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('role', { ascending: true })
        .order('full_name', { ascending: true })

      if (error) throw error

      return (data || []).map(p => ({
        ...p,
        client_ids: p.client_ids || [],
        permissions: p.permissions || {},
        invitation_accepted_at: (p as any).invitation_accepted_at || null,
      }))
    },
    staleTime: 30 * 1000,
  })
}

export function useUpdateProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ProfileMember> & { id: string }) => {
      const { data, error } = await supabase
        .from('profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] })
    },
  })
}

export function useDeleteProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] })
    },
  })
}

/**
 * Real client workspaces, past and present.
 *
 * This used to be a hardcoded allowlist of client names, which meant every new
 * client was invisible in Team Management until someone remembered to edit this
 * file. Global-e and Sathi were both live and both missing, so Alex could not
 * invite their members (2026-09-02). It is now derived from the clients table.
 *
 * Callers that are assigning a NEW workspace filter this to status === 'Active'
 * themselves; the edit dialog deliberately uses the unfiltered list so a member
 * who belongs to a churned workspace (e.g. StoryStream) can still be edited.
 */
const NON_CLIENT_STATUSES = ['deleted', 'system', 'archived', 'not_relevant']
// Internal/demo rows that are not client workspaces. The workspace filter
// already renders its own "Outbound Buddy (Internal)" entry.
const NON_CLIENT_NAMES = ['Outboundbuddy', 'Flowmail']

export function useProductionClients() {
  return useQuery({
    queryKey: ['production-clients'],
    queryFn: async (): Promise<ClientRecord[]> => {
      const { data, error } = await supabase
        .from('clients')
        .select('id, name, client_id, status')
        .not('status', 'in', `(${NON_CLIENT_STATUSES.join(',')})`)
        .not('name', 'in', `(${NON_CLIENT_NAMES.map(n => `"${n}"`).join(',')})`)
        .order('name', { ascending: true })

      if (error) throw error

      // Deduplicate by name (Cruva has 2 entries, use the one with SmartLead ID 167887)
      const seen = new Map<string, ClientRecord>()
      for (const client of data || []) {
        const existing = seen.get(client.name)
        // Prefer UUID-style IDs over numeric string IDs
        if (!existing || (client.id.includes('-') && !existing.id.includes('-'))) {
          seen.set(client.name, client)
        }
      }

      return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name))
    },
    staleTime: 5 * 60 * 1000,
  })
}
